import { Button, Kind } from '@thanx/uikit/button'
import { createCampaignVariant } from 'actions/campaignVariants'
import { alert } from 'actions/flash'
import {
  useCreateUserListCampaignMutation,
  useGetUserListImportHistoryQuery,
  useGetUserListsQuery,
} from 'api/userLists'
import PageHeader from 'components/PageHeader'
import Spinner from 'components/Spinner'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React from 'react'
import { push } from 'react-router-redux'
import {
  confirmImportMode,
  openImporter,
  openImportModeModal,
} from 'slices/userLists'

const t = buildTranslate('user_lists.detail')

type Props = {
  listId: number
  children?: React.ReactNode
}

const DetailHeader: React.FC<Props> = ({ listId, children }) => {
  const dispatch = useDispatch()
  const [createCampaign, { isLoading: isCreating }] =
    useCreateUserListCampaignMutation()
  const { list, isLoading } = useGetUserListsQuery(undefined, {
    selectFromResult: ({ data, isLoading }) => ({
      list: data?.data.find(l => l.id === listId),
      isLoading,
    }),
  })
  const { data: importHistoryData } = useGetUserListImportHistoryQuery({
    userListId: listId,
  })
  const hasExistingImports = (importHistoryData?.data?.length ?? 0) > 0

  if (isLoading) {
    return <Spinner isLoading className="text-center py-xl" />
  }

  const handleImport = () => {
    if (hasExistingImports) {
      dispatch(openImportModeModal(listId))
    } else {
      dispatch(openImporter(listId))
      dispatch(confirmImportMode('update'))
    }
  }

  const handleCreateCampaign = async () => {
    if (!list) {
      dispatch(alert({ key: 'danger', message: t('create_campaign_error') }))
      return
    }
    try {
      const result = await createCampaign({
        userListId: listId,
        listName: list.name,
      }).unwrap()
      const campaignId = result.campaign.id
      // Create a single variant so the builder skips A/B testing
      await dispatch(createCampaignVariant(campaignId))
      dispatch(push(`/thanx_campaigns/targeted/custom/${campaignId}`))
    } catch {
      dispatch(
        alert({
          key: 'danger',
          message: t('create_campaign_error'),
        })
      )
    }
  }

  const headerButtons = (
    <>
      <Button kind={Kind.SECONDARY} className="mr-s" onClick={handleImport}>
        {t('import_customers')}
      </Button>
      <Button
        kind={Kind.PRIMARY}
        onClick={handleCreateCampaign}
        disabled={isCreating}
      >
        <Spinner isLoading={isCreating} size="1x">
          {t('create_campaign')}
        </Spinner>
      </Button>
    </>
  )

  return (
    <PageHeader
      title={list?.name || ''}
      backPath="/user_lists"
      menu={headerButtons}
    >
      {children}
    </PageHeader>
  )
}

export default DetailHeader
